"use client";

// ── The run panel's three stages for a run-twice problem (`EX-RTW03`) ───────
// Board E of the Wave 35 plan: run 1, the jury's check, run 2, one card each,
// for a sample and for custom input alike. What each card shows is what the
// reader can already see or wrote themselves: the sample's or their own
// input, their program's output in each run, and run 2's input, which the
// jury builds from those (`docs/27` §9.36). Nothing of a hidden test reaches
// a sample run, so nothing of one can reach this panel.

import type { ReactNode } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";
import type { SampleRunCaseResult, SampleRunStageRun } from "@/features/quiz/coding-task-api";
import { sampleStageStates, type StageState } from "../../lib/run-twice";
import { formatMegabytes } from "./TestStrip";
import { firstDifferingLine, outputLines } from "./run-output";
import styles from "./run-twice.module.css";

type Translate = ReturnType<typeof useTranslations>;

const CARD_TONE: Readonly<Record<StageState, string>> = {
  waiting: "border-white/10",
  running: "border-primary/70 bg-primary/[0.08]",
  ok: "border-emerald-400/40",
  unchecked: "border-white/20",
  fail: "border-red-400/50 bg-red-400/[0.04]",
  skip: "border-white/10 opacity-50",
};

const BADGE_TONE: Readonly<Record<StageState, string>> = {
  waiting: "bg-white/5 text-white/45",
  running: "bg-primary/20 text-primary-foreground",
  ok: "bg-emerald-400/15 text-emerald-300",
  unchecked: "bg-white/10 text-white/75",
  fail: "bg-red-400/15 text-red-300",
  skip: "bg-white/5 text-white/40",
};

/** The milliseconds between one card coming in and the next. */
const STAGGER_MS = 160;

function Pre({ text, mark, testId }: { text: string; mark?: number | null; testId?: string }) {
  const lines = outputLines(text);
  return (
    <div
      className="max-h-28 min-h-[2rem] overflow-auto rounded-md bg-neutral-950 px-2 py-1 font-mono text-xs leading-5 text-white/85 ring-1 ring-inset ring-white/10"
      {...(testId ? { "data-testid": testId } : {})}
    >
      {lines.length === 0 ? (
        <span className="text-white/35"> </span>
      ) : (
        lines.map((line, i) => (
          <div
            key={i}
            className={cn("whitespace-pre", i === mark && "-mx-2 bg-red-400/15 px-2 text-red-200")}
            {...(i === mark ? { "data-differs": "true" } : {})}
          >
            {line || " "}
          </div>
        ))
      )}
    </div>
  );
}

function Label({ children }: { children: ReactNode }) {
  return <p className="text-[10.5px] font-semibold uppercase tracking-wide text-white/40">{children}</p>;
}

function Note({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "error" | "good" }) {
  return (
    <p
      className={cn(
        "text-xs leading-snug",
        tone === "error" && "font-semibold text-red-300",
        tone === "good" && "text-emerald-300",
        tone === "muted" && "text-white/50",
      )}
    >
      {children}
    </p>
  );
}

function figures(run: SampleRunStageRun | null | undefined, locale: string, tEditor: Translate): string {
  if (!run) return "";
  return [
    run.timeMs != null ? tEditor("figureMs", { ms: run.timeMs }) : null,
    run.memoryKb != null ? tEditor("figureMb", { mb: formatMegabytes(run.memoryKb, locale) }) : null,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");
}

function Card({
  name,
  state,
  badge,
  meta,
  jury = false,
  arrowLit,
  index,
  revealKey,
  children,
  testId,
}: {
  name: string;
  state: StageState;
  badge: string;
  meta?: string;
  jury?: boolean;
  /** Whether the arrow into this card is lit: the stage before it handed something over. */
  arrowLit: boolean | null;
  index: number;
  revealKey: string;
  children: ReactNode;
  testId: string;
}) {
  return (
    <div
      key={revealKey}
      className={cn(
        "flex min-w-0 flex-col gap-1.5 rounded-xl border bg-white/[0.03] p-2.5 transition-colors duration-300",
        jury && "border-dashed",
        CARD_TONE[state],
        revealKey !== "idle" && styles.reveal,
        state === "running" && styles.live,
      )}
      style={revealKey !== "idle" ? { animationDelay: `${index * STAGGER_MS}ms` } : undefined}
      data-testid={testId}
      data-state={state}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {arrowLit !== null ? (
          <ArrowRight
            className={cn("h-3.5 w-3.5 shrink-0 transition-colors duration-300", arrowLit ? "text-primary" : "text-white/20")}
            aria-hidden
          />
        ) : null}
        <span className="text-[13px] font-bold text-white">{name}</span>
        <span
          className={cn(
            "ml-auto inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
            BADGE_TONE[state],
          )}
        >
          {state === "running" ? <Loader2 className="h-3 w-3 motion-safe:animate-spin" aria-hidden /> : null}
          {badge}
        </span>
      </div>
      {meta ? <p className="truncate text-[11px] text-white/45">{meta}</p> : null}
      {children}
    </div>
  );
}

export function RunTwiceStages({
  result,
  running,
  input,
  expected,
  layout,
  locale,
}: {
  result: SampleRunCaseResult | null;
  running: boolean;
  /** The case's input when the panel does not show it elsewhere (a sample); `null` for custom input. */
  input: string | null;
  /** Run 2's expected output on a sample; `null` for custom input, which compares nothing. */
  expected: string | null;
  /** Three across where there is room (samples), one under another beside the custom input box. */
  layout: "row" | "stack";
  locale: string;
}) {
  const t = useTranslations("judge.runTwice");
  const tProblem = useTranslations("judge.problem");
  const tRun = useTranslations("judge.run");
  const tEditor = useTranslations("judge.editor");

  const states = sampleStageStates(result, running);
  const stages = result?.stages ?? null;
  const first = stages?.first ?? null;
  const bridge = stages?.bridge;
  const second = stages?.second;
  // Each answer gets its own key, so the cards come in again for a new run only.
  const key = running || !result ? "idle" : `r${revealIdOf(result)}`;

  const firstBadge =
    states.first === "fail" && first
      ? tProblem(`runOutcome_${first.outcome}`)
      : states.first === "fail"
        ? t("stateFail")
        : badgeOf(states.first, t, "stateOk");
  const bridgeBadge = states.bridge === "fail" ? t("stateBridgeFail") : badgeOf(states.bridge, t, "stateBridgeOk");
  const secondBadge =
    states.second === "fail"
      ? second && second.outcome !== "completed"
        ? tProblem(`runOutcome_${second.outcome}`)
        : t("stateDiffers")
      : badgeOf(states.second, t, "stateMatch");

  const mark =
    second && expected !== null && states.second === "fail" && second.outcome === "completed"
      ? firstDifferingLine(expected, second.stdout)
      : null;

  return (
    <div
      className={cn("grid gap-2", layout === "row" && "sm:grid-cols-3")}
      role="group"
      aria-label={t("tableLabel")}
      data-testid="run-twice-stages"
    >
      <Card
        name={t("stageFirst")}
        state={states.first}
        badge={running ? t("stateRunning") : firstBadge}
        meta={figures(first, locale, tEditor)}
        arrowLit={null}
        index={0}
        revealKey={key}
        testId="run-twice-stage-first"
      >
        {input !== null ? (
          <>
            <Label>{t("input")}</Label>
            <Pre text={input} />
          </>
        ) : null}
        <Label>{t("output")}</Label>
        {running ? (
          <Note>
            <Loader2 className="mr-1 inline h-3 w-3 motion-safe:animate-spin" aria-hidden />
            {tRun("runningOnJudge")}
          </Note>
        ) : first ? (
          first.stdout.trim() === "" ? (
            <Note tone="error">{tRun("printedNothing")}</Note>
          ) : (
            <Pre text={first.stdout} testId="run-twice-first-output" />
          )
        ) : result ? (
          <Note tone="error">{t("notReached")}</Note>
        ) : (
          <Note>{tRun("pressRunSamples")}</Note>
        )}
        {first?.stderr ? <p className="whitespace-pre-wrap font-mono text-[11px] text-red-200/90">{first.stderr}</p> : null}
      </Card>

      <Card
        name={t("stageBridge")}
        state={states.bridge}
        badge={bridgeBadge}
        jury
        arrowLit={states.first === "ok"}
        index={1}
        revealKey={key}
        testId="run-twice-stage-bridge"
      >
        {bridge?.status === "ok" ? (
          <>
            <Note tone="good">{t("bridgeOk")}</Note>
            <Label>{t("secondInput")}</Label>
            <Pre text={bridge.secondInput ?? ""} testId="run-twice-second-input" />
          </>
        ) : bridge?.status === "invalid" ? (
          <>
            <Note tone="error">{t("bridgeInvalid")}</Note>
            {bridge.reason ? (
              <p className="font-mono text-[11px] leading-snug text-red-200/90" data-testid="run-twice-bridge-reason">
                {bridge.reason}
              </p>
            ) : null}
          </>
        ) : bridge?.status === "error" ? (
          <Note tone="error">{t("bridgeError")}</Note>
        ) : states.bridge === "skip" ? (
          <Note>{t("notReached")}</Note>
        ) : (
          <Note>{t("bridgeIdle")}</Note>
        )}
      </Card>

      <Card
        name={t("stageSecond")}
        state={states.second}
        badge={secondBadge}
        meta={figures(second, locale, tEditor)}
        arrowLit={states.bridge === "ok"}
        index={2}
        revealKey={key}
        testId="run-twice-stage-second"
      >
        <Label>{t("output")}</Label>
        {second ? (
          second.stdout.trim() === "" ? (
            <Note tone="error">{tRun("printedNothing")}</Note>
          ) : (
            <Pre text={second.stdout} mark={mark} testId="run-twice-second-output" />
          )
        ) : states.second === "skip" ? (
          <Note>{t("notReached")}</Note>
        ) : (
          <Note>{t("stateWaiting")}</Note>
        )}
        {second?.stderr ? <p className="whitespace-pre-wrap font-mono text-[11px] text-red-200/90">{second.stderr}</p> : null}
        {expected !== null ? (
          <>
            <Label>{t("expected")}</Label>
            <Pre text={expected} />
          </>
        ) : null}
      </Card>
    </div>
  );
}

const revealIds = new WeakMap<object, number>();
let nextRevealId = 1;

function revealIdOf(result: object): number {
  let id = revealIds.get(result);
  if (id === undefined) {
    id = nextRevealId++;
    revealIds.set(result, id);
  }
  return id;
}

function badgeOf(state: StageState, t: Translate, okKey: string): string {
  switch (state) {
    case "running":
      return t("stateRunning");
    case "ok":
      return t(okKey);
    case "unchecked":
      return t("stateUnchecked");
    case "skip":
      return t("stateSkip");
    case "fail":
      return t("stateFail");
    default:
      return t("stateWaiting");
  }
}
